import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	ChangeDetectorRef,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ElementRef,
	OnInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as fuzzysort from 'fuzzysort';
import type { AllowedOrg, EmailVerificationResponse } from '@go-modules/enums/email-verification-response';
import type { SignupOrg } from '@dashboard/app/interfaces/sign-up/signup-orgs-response';
import { UserType, UserTypeSelectionDialogComponent } from '@go-modules/components/dialogs/user-type-selection-dialog/user-type-selection-dialog.component';
import { EMPTY, Subject } from 'rxjs';
import { shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { OrgTypesService } from '@go-modules/services/org-types/org-types.service';

@Component({
	selector: 'org-search',
	templateUrl: './org-search.component.html',
	styleUrls: [
		'./org-search.component.scss',
		'./org-search-sm.component.scss',
		'./org-search-xs.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class OrgSearchComponent implements OnInit, OnChanges, OnDestroy {
	@Input()
	public emailVerificationData: EmailVerificationResponse;
	@Input()
	public allOrgs: SignupOrg[];
	@Input()
	public allowedOrgs: AllowedOrg[];
	@Output()
	public onBack = new EventEmitter<void>(); // eslint-disable-line @angular-eslint/no-output-on-prefix
	@Output()
	public onCreateOrg = new EventEmitter<{searchTerm: string}>(); // eslint-disable-line @angular-eslint/no-output-on-prefix
	@Output()
	public onSelectOrg = new EventEmitter<SignupOrg>(); // eslint-disable-line @angular-eslint/no-output-on-prefix
	@Output()
	public reloadOrgs = new EventEmitter<void>();
	@Output()
	public navigateToStudentStep = new EventEmitter<void>();

	public searchResults;
	public defaultInvites = [];
	public searchTerm = '';
	public searchCountTranslation = '';
	public totalSearchResults = 0;
	public searchResultLimit = 100;

	private orgTypes$ = this.orgTypesService.getOrgTypes().pipe(
		shareReplay(1)
	);

	private componentDestroyed: Subject<boolean> = new Subject();

	constructor (
		private cd: ChangeDetectorRef,
		private ngxTranslate: TranslateService,
		private elementRef: ElementRef,
		private orgTypesService: OrgTypesService,
		public dialog: MatDialog
	) {}

	public ngOnInit (): void {
		this.orgTypes$.pipe(
			takeUntil(this.componentDestroyed)
		).subscribe();
	}

	public ngOnChanges (_changes: SimpleChanges): void {
		const inviteOrgs = this.emailVerificationData?.invited_orgs ?? [] as AllowedOrg[];
		let allowedOrgs = this.allowedOrgs ?? [];

		allowedOrgs = allowedOrgs.filter((allowedOrg) => {
			return !inviteOrgs.find((inviteOrg) => inviteOrg.group_id === allowedOrg.group_id);
		});

		this.defaultInvites = inviteOrgs.concat(allowedOrgs);

		if (!!this.allOrgs && this.defaultInvites.length > 0) {
			this.defaultInvites.forEach((di) => {
				if (!this.allOrgs.find((o) => o.group_id === di.group_id)) {
					const newOrg: SignupOrg = di;
					di.open_access = true;
					this.allOrgs.push(newOrg);
				}
			});
			this.allOrgs.forEach((o) => {
				// allow joining any orgs that permit based on your email domain or your invites.
				o.open_access = o.open_access || !!this.defaultInvites.find((di) => di.group_id === o.group_id);
			});
		}
		// If there was an update, re-search
		this.search();
		this.cd.detectChanges();
	}

	public ngOnDestroy (): void {
		this.componentDestroyed.next(true);
		this.componentDestroyed.complete();
	}

	public search (): void {
		if (this.searchTerm === '') {
			this.searchResults = null;
		} else {
			const options = { allowTypo: true, keys: ['name'] };
			this.searchResults = fuzzysort
				.go(this.searchTerm, this.allOrgs, options)
				.map((result) => result.obj);
			this.totalSearchResults = this.searchResults.length;
			// Truncate for performance reasons.
			this.searchResults = this.searchResults.slice(0, this.searchResultLimit);
		}
		const translation = !this.searchResults || this.totalSearchResults === 1 ?
			this.ngxTranslate.get('sign-up-org-search_search-result-one-count') :
			this.ngxTranslate.get('sign-up-org-search_search-result-plural-count', {count: this.totalSearchResults});
		translation
			.pipe(take(1))
			.pipe(takeUntil(this.componentDestroyed))
			.subscribe((val) => {
				this.searchCountTranslation = val;
				this.cd.detectChanges();
			});
	}

	public startSearch () {
		this.defaultInvites = [];
		this.cd.detectChanges();
		this.elementRef.nativeElement.querySelector('input').focus();
	}

	public clearSearch () {
		this.searchTerm = '';
		this.searchResults = null;
		this.elementRef.nativeElement.querySelector('input').focus();
		this.cd.detectChanges();
	}

	public selectOrg (selectedOrg: SignupOrg) {
		this.orgTypes$.pipe(
			switchMap((orgTypes) => {
				const matchedOrgType = orgTypes.find(
					(orgType) => orgType.id === selectedOrg.org_type_id
				);

				if (!matchedOrgType?.student_pay_enabled) {
					this.onSelectOrg.emit(selectedOrg);
					return EMPTY;
				}

				return this.dialog.open(UserTypeSelectionDialogComponent)
					.afterClosed();
			})
		).subscribe((userType: UserType) => {
			if (userType === UserType.Instructor) {
				this.onSelectOrg.emit(selectedOrg);
			} else if (userType === UserType.Presenter) {
				this.navigateToStudentStep.emit();
			}
		});
	}


	public createOrg () {
		this.onCreateOrg.emit({searchTerm: this.searchTerm});
	}

	public back () {
		this.onBack.emit();
	}
}
